import dayjs from 'dayjs'
import buddhistEra from 'dayjs/plugin/buddhistEra.js'
import customParseFormat from 'dayjs/plugin/customParseFormat.js'
import relativeTime from 'dayjs/plugin/relativeTime.js'
import 'dayjs/locale/th.js'
import ThaiBahtText from 'thai-baht-text'

dayjs.extend(buddhistEra)
dayjs.extend(customParseFormat)
dayjs.extend(relativeTime)

export type Locale = 'th' | 'en'

const intlLocale = (locale: Locale) => (locale === 'th' ? 'th-TH' : 'en-US')

export function formatNumber(
  value: number | null | undefined,
  { locale = 'th', digits }: { locale?: Locale; digits?: number } = {},
): string {
  if (value == null || Number.isNaN(value)) return ''
  return new Intl.NumberFormat(intlLocale(locale), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits ?? 2,
  }).format(value)
}

/**
 * 1234.5 → "1,234.50" (amounts keep two decimals, as on Thai documents); `whole`: a whole amount without them (1200 →
 * "1,200"), for lists where satang are rare
 */
export function formatMoney(
  value: number | null | undefined,
  { locale = 'th', whole = false }: { locale?: Locale; whole?: boolean } = {},
): string {
  return formatNumber(value, { locale, digits: whole && Number.isInteger(value) ? 0 : 2 })
}

export type DateStyle = 'short' | 'medium' | 'long' | 'day'

const dateFormats: Record<Locale, Record<DateStyle, string>> = {
  // BBBB = Buddhist-era year (2569), the norm on Thai documents
  // `day` names the weekday too, as a clock does: วันอังคารที่ 3 พฤศจิกายน 2569
  th: { short: 'DD/MM/BBBB', medium: 'D MMM BBBB', long: 'D MMMM BBBB', day: '[วัน]dddd[ที่] D MMMM BBBB' },
  en: { short: 'DD/MM/YYYY', medium: 'D MMM YYYY', long: 'D MMMM YYYY', day: 'dddd D MMMM YYYY' },
}

export function dateFormat(locale: Locale = 'th', style: DateStyle = 'medium'): string {
  return dateFormats[locale][style]
}

/**
 * A typed date into YYYY-MM-DD: day, month and year with / . or - between, the day and month with or without a 0
 * (05/02/2570, 5/2/2570), the year Buddhist era (2569, or 69) or Gregorian (2026); null for anything else
 */
export function parseThaiDate(input: string): string | null {
  const m = input.trim().match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/)
  if (!m?.[1] || !m[2] || !m[3]) return null
  let year = Number(m[3])
  if (year < 100) year += 2500 // "69" means 2569 BE
  if (year > 2400) year -= 543
  const iso = `${year}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`
  return dayjs(iso, 'YYYY-MM-DD', true).isValid() ? iso : null
}

type DateInput = string | number | Date | null | undefined

interface DateOptions {
  locale?: Locale
  style?: DateStyle
}

export function formatDate(value: DateInput, { locale = 'th', style = 'medium' }: DateOptions = {}): string {
  if (value == null || value === '') return ''
  const d = dayjs(value)
  return d.isValid() ? d.locale(locale).format(dateFormat(locale, style)) : ''
}

/** A month (`2026-10`) as people say it: ตุลาคม 2569, October 2026 */
export function formatMonth(month: string, { locale = 'th' }: { locale?: Locale } = {}): string {
  const d = dayjs(`${month}-01`)
  return d.isValid() ? d.locale(locale).format(locale === 'th' ? 'MMMM BBBB' : 'MMMM YYYY') : ''
}

export function formatTime(value: DateInput): string {
  if (value == null || value === '') return ''
  if (typeof value === 'string' && /^\d{2}:\d{2}/.test(value)) return value.slice(0, 5)
  const d = dayjs(value)
  return d.isValid() ? d.format('HH:mm') : ''
}

export function formatDateTime(value: DateInput, { locale = 'th', style = 'medium' }: DateOptions = {}): string {
  const date = formatDate(value, { locale, style })
  return date ? `${date} ${formatTime(value)}` : ''
}

export function fromNow(value: DateInput, { locale = 'th' }: { locale?: Locale } = {}): string {
  if (value == null || value === '') return ''
  return dayjs(value).locale(locale).fromNow()
}

/** Amount in Thai words for invoices and receipts: 1234.5 → "หนึ่งพันสองร้อยสามสิบสี่บาทห้าสิบสตางค์" */
export function bahtText(value: number): string {
  return ThaiBahtText(Math.round(value * 100) / 100)
}

/** 0105536000000 → 0-1055-36000-00-0 */
export function formatTaxId(value: string | null | undefined): string {
  const d = (value ?? '').replace(/\D/g, '')
  if (d.length !== 13) return value ?? ''
  return `${d[0]}-${d.slice(1, 5)}-${d.slice(5, 10)}-${d.slice(10, 12)}-${d[12]}`
}

/**
 * A tax branch as tax invoices print it: five digits, 00000 the head office. Spreadsheets drop the zeros (0 → 00000,
 * 1 → 00001) and people write the head office out (สำนักงานใหญ่, สนญ.); anything else stays as given
 */
export function taxBranch(value: unknown): unknown {
  const text = typeof value === 'number' && Number.isInteger(value) ? String(value) : value
  if (typeof text !== 'string') return value
  if (/^\d{1,5}$/.test(text.trim())) return text.trim().padStart(5, '0')
  if (/^(สำนักงานใหญ่|สนญ\.?|head ?office|hq)$/i.test(text.trim())) return '00000'
  return value
}

/** A Thai ID or tax ID (13 digits, people's and companies' alike) whose last digit checks the other twelve */
export function thaiIdValid(id: string): boolean {
  if (!/^\d{13}$/.test(id)) return false
  let sum = 0
  for (let i = 0; i < 12; i++) sum += Number(id[i]) * (13 - i)
  return (11 - (sum % 11)) % 10 === Number(id[12])
}

/** An account number as passbooks print it, separators aside: 012-3-45678-9 → 0123456789 */
export const accountNumber = (value: unknown): unknown => (typeof value === 'string' ? value.replace(/[\s.-]/g, '') : value)

/** 0812345678 → 081-234-5678, 021234567 → 02-123-4567 */
export function formatPhone(value: string | null | undefined): string {
  const d = (value ?? '').replace(/\D/g, '')
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`
  if (d.length === 9) return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`
  return value ?? ''
}

/** What divides the numbers of one phone field: `081-234-5678, 02-123-4567` */
export const phoneSeparators = /[,/;]/

/** The whole numbers in a phone field, digits only: 9 digits (a landline) or more */
export const phoneNumbers = (value: string | null | undefined): string[] =>
  (value ?? '')
    .split(phoneSeparators)
    .map((part) => part.replace(/\D/g, ''))
    .filter((digits) => digits.length >= 9)

export interface ThaiArea {
  subdistrict?: string | null
  district?: string | null
  province?: string | null
  postcode?: string | null
}

/** ต.ท่าศาลา อ.ท่าศาลา จ.นครศรีธรรมราช 80160; Bangkok reads แขวง… เขต… กรุงเทพมหานคร */
export function formatArea(area: ThaiArea): string {
  const bangkok = area.province === 'กรุงเทพมหานคร'
  return [
    area.subdistrict && `${bangkok ? 'แขวง' : 'ต.'}${area.subdistrict}`,
    area.district && `${bangkok ? 'เขต' : 'อ.'}${area.district}`,
    area.province && (bangkok ? area.province : `จ.${area.province}`),
    area.postcode,
  ]
    .filter(Boolean)
    .join(' ')
}

/** Today in the server's or browser's local zone, as YYYY-MM-DD */
export function today(): string {
  return dayjs().format('YYYY-MM-DD')
}

export { dayjs }
